import React, {useCallback} from 'react'
import Dropzone, {DropEvent, DropzoneState, FileRejection} from 'react-dropzone'
import styled from 'styled-components'
import {MEDIA_UPLOAD} from 'consts/common'
import convertUnit from 'lib/unit'
import {showSnackbar} from 'utils/components'
import {translate} from 'i18n'
import {DragDropProps} from './DragDropProps'
import {Paragraph} from '../../texts'
import {Icon} from '../../icons'

interface StyledContainerProps {
  isDragActive: boolean
}

const StyledContainer = styled.div<StyledContainerProps>`
  ${({theme, isDragActive}) => ({
    color: isDragActive ? theme.gray_5 : theme.gray_3,
  })}
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  cursor: pointer;
  border-radius: 8px;
  transition: border 0.1s ease-out;
  line-height: 1.5;
  outline: none;
`

const StyledLabel = styled(Paragraph)`
  transition: all 0.1s ease-out;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
`

const StyledDesc = styled(Paragraph)`
  margin-bottom: ${convertUnit(5)};
  transition: all 0.1s ease-out;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default function DragDrop({
  accept = MEDIA_UPLOAD,
  header,
  description,
  icon,
  limit,
  subdescription,
  onDrop,
  ...props
}: DragDropProps) {
  const handleDrop = useCallback(
    (
      acceptedFiles: File[],
      fileRejections: FileRejection[],
      event: DropEvent,
    ) => {
      if (!onDrop) return

      if (limit && acceptedFiles.length > limit) {
        showSnackbar(translate('giftShop:uploadIconMaxSnackbar', {max: limit}))
        return
      }

      onDrop(acceptedFiles, fileRejections, event)
    },
    [limit, onDrop],
  )

  const handleRenderChildren = useCallback(
    ({isDragActive, getRootProps, getInputProps}: DropzoneState) => (
      <StyledContainer {...getRootProps()} isDragActive={isDragActive}>
        <input {...getInputProps()} />
        {icon && <Icon type={icon} color="primary_5" size={34} />}{' '}
        {typeof header === 'string' ? (
          <StyledLabel fontSize="xl" fontWeight="bold" color="primary_5">
            {header}
          </StyledLabel>
        ) : (
          header
        )}
        {!isDragActive && typeof description === 'string' && (
          <StyledDesc fontSize="m" fontWeight="medium" color="gray_5">
            {description}
          </StyledDesc>
        )}
        {!isDragActive && typeof description === 'object' && description}
        {!isDragActive && typeof subdescription === 'string' && (
          <StyledDesc fontSize="s" color="gray_5">
            {subdescription}
          </StyledDesc>
        )}
        {!isDragActive && typeof subdescription === 'object' && subdescription}
      </StyledContainer>
    ),
    [description, header, icon, subdescription],
  )

  return (
    <Dropzone {...props} accept={accept} onDrop={handleDrop}>
      {handleRenderChildren}
    </Dropzone>
  )
}
